// =====================================================================
// Unified error normalisation (CODE_STYLE §7, STACK §7).
// Every thrown error (edge-function JSON, network failure, unknown) is
// reduced to a stable `code` that maps to an i18n key `errors.<code>`.
// The global mutation onError (query-client) renders it via sonner.
// =====================================================================

export interface AppError {
  /** Stable code → i18n key `errors.<code>`. */
  code: string;
  /** Optional server-provided message (fallback when no translation). */
  message: string;
}

/** Shape returned by Edge Functions (_shared/errors.ts): { ok:false, code, message }. */
function isEdgeError(v: unknown): v is { code: string; message?: string } {
  return (
    typeof v === "object" && v !== null &&
    "code" in v && typeof (v as { code: unknown }).code === "string"
  );
}

export function toAppError(error: unknown): AppError {
  // Edge function already threw a structured payload.
  if (isEdgeError(error)) {
    return { code: error.code, message: error.message ?? "" };
  }
  // Error instance carrying a JSON body in its message (fetch wrappers).
  if (error instanceof Error) {
    try {
      const parsed: unknown = JSON.parse(error.message);
      if (isEdgeError(parsed)) return { code: parsed.code, message: parsed.message ?? "" };
    } catch {
      /* not JSON — fall through */
    }
    if (/fetch|network|timeout|abort/i.test(error.message)) {
      return { code: "network", message: error.message };
    }
    return { code: "internal", message: error.message };
  }
  return { code: "internal", message: "" };
}
